import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  VolunteerActivism as VolunteerIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: <EventIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Event Management',
      description: 'Create and manage volunteer events with labeled slots and flexible scheduling.',
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Smart Scheduling',
      description: 'Volunteers can self-signup or be assigned by admins. Request replacements with ease.',
    },
    {
      icon: <NotificationsIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Real-time Notifications',
      description: 'Stay updated with instant notifications for assignments, replacements, and reminders.',
    },
    {
      icon: <GroupIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Organization Isolation',
      description: 'Each organization has its own secure space with complete volunteer management.',
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Analytics Dashboard',
      description: 'Track volunteer hours, event participation, and organizational impact.',
    },
    {
      icon: <VolunteerIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Volunteer Profiles',
      description: 'Manage skills, availability, and track your volunteer journey.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 5, md: 12 },
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '2rem', md: '3.5rem' },
                }}
              >
                Connect. Volunteer. Make an Impact.
              </Typography>
              <Typography
                variant="h6"
                paragraph
                sx={{ mb: { xs: 3, md: 4 }, opacity: 0.9, fontSize: { xs: '1rem', md: '1.25rem' } }}
              >
                ServeSync helps organizations manage volunteers and create meaningful
                community impact through seamless event coordination.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(isAuthenticated() ? '/dashboard' : '/register')}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    minHeight: 52,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  {isAuthenticated() ? 'Go to Dashboard' : 'Get Started'}
                </Button>
                {!isAuthenticated() && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/login')}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      minHeight: 52,
                      '&:hover': { borderColor: 'grey.100', bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    Sign In
                  </Button>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <VolunteerIcon sx={{ fontSize: 300, opacity: 0.2 }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          sx={{ mb: 2, fontWeight: 'bold' }}
        >
          Everything You Need
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          paragraph
          sx={{ mb: 6 }}
        >
          Powerful features to streamline volunteer management
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action Section */}
      <Box
        sx={{
          bgcolor: 'grey.100',
          py: { xs: 6, md: 8 },
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Ready to Make a Difference?
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4 }}>
              Join thousands of volunteers and organizations making an impact in their communities.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
              >
                Sign Up Now
              </Button>
              {!isAuthenticated() && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
              )}
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
